<template>
  <div class="expanded-row-content">
    <div class="controls-header">
      <button
        class="toggle-all-button"
        @click="toggleAllCards">
        <i :class="allExpanded ? 'fas fa-compress-alt' : 'fas fa-expand-alt'" />
        {{ allExpanded ? 'Collapse All' : 'Expand All' }}
      </button>
    </div>

    <!-- Fulfillment Orders Card - Added at the top for quick access -->
    <CollapsibleCard
      ref="fulfillmentOrdersCard"
      :default-expanded="true">
      <template #title>
        <i class="fas fa-truck text-amber-600 mr-2" />
        Fulfillment Orders
      </template>
      <FulfillmentOrdersCard :organization-id="organizationId" />
    </CollapsibleCard>

    <!-- Comparison Pages Card - Added below fulfillment orders for quick access -->
    <CollapsibleCard
      ref="comparisonPagesCard"
      :default-expanded="true">
      <template #title>
        <i class="fas fa-chart-bar text-purple-600 mr-2" />
        Comparison Pages
      </template>
      <ComparisonPagesCard :organization-id="organizationId" />
    </CollapsibleCard>

    <!-- Triage Tickets Card -->
    <CollapsibleCard
      ref="triageTicketsCard"
      :default-expanded="true">
      <template #title>
        <i class="fas fa-ticket-alt text-red-600 mr-2" />
        Triage Tickets
      </template>
      <TriageTicketsCard :organization-id="organizationId" />
    </CollapsibleCard>

    <CollapsibleCard
      ref="locationsGbpCard"
      :default-expanded="true">
      <template #title>
        <i class="fas fa-map-marker-alt text-green-600 mr-2" />
        Locations & GBP Checklists
      </template>
      <LocationsGbpCard :organization-id="organizationId" />
    </CollapsibleCard>

    <CollapsibleCard
      ref="auditReportsCard"
      :default-expanded="false">
      <template #title>
        <i class="fas fa-chart-line text-indigo-600 mr-2" />
        Performance Reports
      </template>
      <AuditReportsCard :organization-id="organizationId" />
    </CollapsibleCard>

    <CollapsibleCard
      ref="spotlightCard"
      :default-expanded="false">
      <template #title>
        <i class="fas fa-broadcast-tower text-purple-600 mr-2" />
        Spotlight Tracking
      </template>
      <SpotlightTracking :organization-id="organizationId" />
    </CollapsibleCard>

    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <CollapsibleCard
        ref="card1"
        :default-expanded="true">
        <template #title>
          <i class="fas fa-user text-blue-600 mr-2" />
          Primary Contact
        </template>
        <PrimaryContactForm :organization-id="organizationId" />
      </CollapsibleCard>

      <CollapsibleCard
        ref="card2"
        :default-expanded="true">
        <template #title>
          <i class="fas fa-users text-purple-600 mr-2" />
          Assigned Team Members
        </template>
        <AssignedTeamMembers :organization-id="organizationId" />
      </CollapsibleCard>

      <CollapsibleCard
        ref="card3"
        :default-expanded="true">
        <template #title>
          <i class="fas fa-globe text-indigo-600 mr-2" />
          Website Information
        </template>
        <WebsiteInformationForm :organization-id="organizationId" />
      </CollapsibleCard>

      <CollapsibleCard
        ref="card4"
        :default-expanded="true">
        <template #title>
          <i class="fas fa-dollar-sign text-green-600 mr-2" />
          Sales Information
        </template>
        <SalesInformationForm :organization-id="organizationId" />
      </CollapsibleCard>
    </div>

    <CollapsibleCard
      ref="card5"
      :default-expanded="true">
      <template #title>
        <i class="fas fa-rocket text-emerald-600 mr-2" />
        Activation Stage
      </template>
      <ActivationStageForm :organization-id="organizationId" />
    </CollapsibleCard>

    <!-- Onboarding Stage - Hidden for now -->
    <CollapsibleCard
      ref="card6"
      :default-expanded="true">
      <template #title>
        <i class="fas fa-user-check text-blue-600 mr-2" />
        Onboarding Stage
      </template>
      <OnboardingStageForm :organization-id="organizationId" />
    </CollapsibleCard>

    <CollapsibleCard
      ref="card7"
      :default-expanded="true">
      <template #title>
        <i class="fas fa-chart-line text-orange-600 mr-2" />
        Performance Stage
      </template>
      <PerformanceStageForm :organization-id="organizationId" />
    </CollapsibleCard>

    <CollapsibleCard
      ref="card8"
      :default-expanded="true">
      <template #title>
        <i class="fas fa-comments text-indigo-600 mr-2" />
        Intercom Analytics
      </template>
      <IntercomAnalytics :organization-id="organizationId" />
    </CollapsibleCard>

    <CollapsibleCard
      ref="card9"
      :default-expanded="true">
      <template #title>
        <i class="fas fa-credit-card text-green-600 mr-2" />
        Stripe Financial Data
      </template>
      <StripeDataCard :organization-id="organizationId" />
    </CollapsibleCard>

    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
      <CollapsibleCard
        ref="card10"
        :default-expanded="true">
        <template #title>
          <i class="fas fa-headset text-blue-600 mr-2" />
          GoHighLevel Lead Contact
        </template>
        <GhlLeadContactCard :organization-id="organizationId" />
      </CollapsibleCard>

      <CollapsibleCard
        ref="card11"
        :default-expanded="true">
        <template #title>
          <i class="fas fa-life-ring text-purple-600 mr-2" />
          GoHighLevel Support Contact
        </template>
        <GhlSupportContactCard :organization-id="organizationId" />
      </CollapsibleCard>
    </div>

    <CollapsibleCard
      ref="card12"
      :default-expanded="true">
      <template #title>
        <i class="fas fa-map-marked-alt text-teal-600 mr-2" />
        Geogrid Intelligence
      </template>
      <GeogridIntelligenceCard :organization-id="organizationId" />
    </CollapsibleCard>

    <CollapsibleCard
      ref="card13"
      :default-expanded="true">
      <template #title>
        <i class="fas fa-map-pin text-rose-600 mr-2" />
        Checkins Analytics
      </template>
      <CheckinsAnalyticsCard :organization-id="organizationId" />
    </CollapsibleCard>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import CollapsibleCard from './collapsible_card.vue'
import FulfillmentOrdersCard from './fulfillment_orders_card.vue'
import ComparisonPagesCard from './comparison_pages_card.vue'
import LocationsGbpCard from './locations_gbp_card.vue'
import AuditReportsCard from './audit_reports_card.vue'
import SpotlightTracking from './spotlight_tracking.vue'
import PrimaryContactForm from './primary_contact_form.vue'
import AssignedTeamMembers from './assigned_team_members.vue'
import WebsiteInformationForm from './website_information_form.vue'
import SalesInformationForm from './sales_information_form.vue'
import ActivationStageForm from './activation_stage_form.vue'
import OnboardingStageForm from './onboarding_stage_form.vue' // Hidden for now
import PerformanceStageForm from './performance_stage_form.vue'
import IntercomAnalytics from './intercom_analytics.vue'
import StripeDataCard from './stripe_data_card.vue'
import GhlLeadContactCard from './admin/ghl/lead_contact_card_lazy.vue'
import GhlSupportContactCard from './admin/ghl/support_contact_card_lazy.vue'
import GeogridIntelligenceCard from './geogrid_intelligence_card.vue'
import CheckinsAnalyticsCard from './checkins_analytics_card.vue'
import TriageTicketsCard from './triage_tickets_card.vue'

defineProps({
  organizationId: {
    type: String,
    required: true
  }
})

// Refs
const fulfillmentOrdersCard = ref(null)
const comparisonPagesCard = ref(null)
const locationsGbpCard = ref(null)
const auditReportsCard = ref(null)
const spotlightCard = ref(null)
const card1 = ref(null)
const card2 = ref(null)
const card3 = ref(null)
const card4 = ref(null)
const card5 = ref(null)
const card6 = ref(null)
const card7 = ref(null)
const card8 = ref(null)
const card9 = ref(null)
const card10 = ref(null)
const card11 = ref(null)
const card12 = ref(null)
const card13 = ref(null)

const allExpanded = ref(true)

// Methods
const toggleAllCards = () => {
  const newState = !allExpanded.value
  const allCards = [
    fulfillmentOrdersCard.value,
    comparisonPagesCard.value,
    locationsGbpCard.value,
    auditReportsCard.value,
    spotlightCard.value,
    card1.value, card2.value, card3.value, card4.value, card5.value,
    card6.value,
    card7.value, card8.value, card9.value, card10.value,
    card11.value, card12.value, card13.value
  ]
  allCards.forEach((card) => {
    if (card && card.setExpanded) {
      card.setExpanded(newState)
    }
  })
  allExpanded.value = newState
}
</script>

<style scoped>
.expanded-row-content {
  @apply px-4 py-2;
}

.controls-header {
  @apply flex justify-end mb-4;
}

.toggle-all-button {
  @apply inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700
         bg-white border border-gray-300 rounded-md hover:bg-gray-50
         focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500
         transition-colors duration-200;
}

.toggle-all-button i {
  @apply mr-1.5;
}
</style>
